import { useState, useEffect } from 'react';
import type { MonthlyDuration } from '../types';

interface MonthlyDurationManagerProps {
  durations: MonthlyDuration[];
  onSave: (durations: MonthlyDuration[]) => void;
  unlimitedPackagePrice: number;
}

export default function MonthlyDurationManager({
  durations,
  onSave,
  unlimitedPackagePrice,
}: MonthlyDurationManagerProps) {
  const [localDurations, setLocalDurations] = useState<MonthlyDuration[]>([]);

  useEffect(() => {
    if (durations.length > 0) {
      setLocalDurations(durations);
    } else {
      // 初始化1-12个月的时长
      const initialDurations: MonthlyDuration[] = Array.from({ length: 12 }, (_, i) => ({
        months: i + 1,
        isEnabled: false,
      }));
      setLocalDurations(initialDurations);
    }
  }, [durations]);

  const handleToggle = (months: number, isEnabled: boolean) => {
    const updated = localDurations.map((d) => (d.months === months ? { ...d, isEnabled } : d));
    setLocalDurations(updated);
    onSave(updated);
  };

  const handleDiscountChange = (
    months: number,
    type: 'gift' | 'reduction' | 'discount' | null,
    value: number,
    unit?: string
  ) => {
    const updated = localDurations.map((d) => {
      if (d.months === months) {
        if (type === null) {
          const { discount, ...rest } = d;
          return rest;
        }
        return { ...d, discount: { type, value, unit } };
      }
      return d;
    });
    setLocalDurations(updated);
    onSave(updated);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-4">时长管理</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">时长</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">开关</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">优惠类型</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">优惠值</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {localDurations.map((duration) => (
              <tr key={duration.months}>
                <td className="px-3 py-2 text-gray-900">{duration.months}个月</td>
                <td className="px-3 py-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={duration.isEnabled}
                      onChange={(e) => handleToggle(duration.months, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-500"></div>
                  </label>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={duration.discount?.type || ''}
                    onChange={(e) => {
                      const type = e.target.value as 'gift' | 'reduction' | 'discount' | '';
                      if (type) {
                        handleDiscountChange(duration.months, type, 0);
                      } else {
                        handleDiscountChange(duration.months, null, 0);
                      }
                    }}
                    disabled={!duration.isEnabled}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">无优惠</option>
                    <option value="gift">赠送</option>
                    <option value="reduction">减免</option>
                    <option value="discount">折扣</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  {duration.discount && (
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        step={duration.discount.type === 'discount' ? '0.1' : '1'}
                        value={duration.discount.value}
                        onChange={(e) =>
                          handleDiscountChange(
                            duration.months,
                            duration.discount!.type,
                            Number(e.target.value),
                            duration.discount!.unit
                          )
                        }
                        disabled={!duration.isEnabled}
                        className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      {duration.discount.type === 'gift' && (
                        <select
                          value={duration.discount.unit || 'days'}
                          onChange={(e) =>
                            handleDiscountChange(
                              duration.months,
                              duration.discount!.type,
                              duration.discount!.value,
                              e.target.value
                            )
                          }
                          disabled={!duration.isEnabled}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="days">天</option>
                          <option value="hours">小时</option>
                        </select>
                      )}
                      {duration.discount.type === 'reduction' && (
                        <span className="text-gray-600">元</span>
                      )}
                      {duration.discount.type === 'discount' && (
                        <span className="text-gray-600">折</span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {unlimitedPackagePrice === 0 && (
        <p className="text-xs text-amber-600 mt-3">
          提示：请先在套餐管理中配置"不限时长"套餐的价格
        </p>
      )}
    </div>
  );
}
